import {
  ArrowRight,
  BadgeCheck,
  Check,
  CircleDollarSign,
  FileSignature,
  Handshake,
  HeartHandshake,
  MapPin,
  MessageCircle,
  Search,
  Send,
  Sparkles,
  UserRoundCheck,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { athletes, briefs, companies } from '../../data/demo'
import { currency } from '../../lib/format'

type JourneyRole = 'company' | 'athlete'

type JourneyStep = {
  id: string
  label: string
  companyTitle: string
  athleteTitle: string
  companyCopy: string
  athleteCopy: string
  companyAction: string
  athleteAction: string
  result: string
}

const steps: JourneyStep[] = [
  {
    id: 'discover',
    label: 'Découvrir',
    companyTitle: 'Rechercher un athlète',
    athleteTitle: 'Voir les opportunités',
    companyCopy: 'Filtrez les profils par sport, ville, budget et disponibilité.',
    athleteCopy: 'Parcourez des briefs avec un budget, une durée et une date limite.',
    companyAction: 'Découvrir les athlètes',
    athleteAction: 'Voir les opportunités',
    result: 'Vous identifiez rapidement les profils et briefs qui correspondent à votre besoin.',
  },
  {
    id: 'interest',
    label: 'Montrer son intérêt',
    companyTitle: 'Enregistrer ou inviter',
    athleteTitle: 'Répondre à un brief',
    companyCopy: 'Ajoutez un profil à votre sélection ou envoyez une invitation.',
    athleteCopy: 'Posez une question ou envoyez votre intérêt à l’entreprise.',
    companyAction: 'Ajouter à la sélection',
    athleteAction: 'Je suis intéressé',
    result: 'L’autre partie reçoit un signal clair, sans devoir accepter un contrat immédiatement.',
  },
  {
    id: 'chat',
    label: 'Échanger',
    companyTitle: 'Commencer une conversation',
    athleteTitle: 'Préciser votre projet',
    companyCopy: 'Discutez du calendrier, des attentes et des formats possibles.',
    athleteCopy: 'Présentez votre saison, vos besoins et vos disponibilités.',
    companyAction: 'Ouvrir la conversation',
    athleteAction: 'Répondre au message',
    result: 'Le contexte du profil ou du brief reste attaché à la conversation.',
  },
  {
    id: 'agreement',
    label: 'Construire l’accord',
    companyTitle: 'Préparer une proposition',
    athleteTitle: 'Négocier les contreparties',
    companyCopy: 'Définissez le budget, la durée, les livrables et les droits d’image.',
    athleteCopy: 'Ajustez les contenus, les échéances et les modalités de paiement.',
    companyAction: 'Envoyer la proposition',
    athleteAction: 'Faire une contre-proposition',
    result: 'Les deux parties valident les mêmes conditions avant la contractualisation.',
  },
  {
    id: 'partnership',
    label: 'Lancer le partenariat',
    companyTitle: 'Signer et suivre',
    athleteTitle: 'Démarrer la collaboration',
    companyCopy: 'Après accord, le contrat, le paiement et les livrables deviennent accessibles.',
    athleteCopy: 'Suivez vos échéances, déposez vos preuves et consultez les paiements.',
    companyAction: 'Voir le partenariat',
    athleteAction: 'Voir mes livrables',
    result: 'Le partenariat passe en statut actif avec un calendrier et des responsabilités visibles.',
  },
]

export function InteractiveJourney({ pageMode = false }: { pageMode?: boolean }) {
  const [role, setRole] = useState<JourneyRole>('company')
  const [activeIndex, setActiveIndex] = useState(0)
  const step = steps[activeIndex]
  const title = role === 'company' ? step.companyTitle : step.athleteTitle
  const copy = role === 'company' ? step.companyCopy : step.athleteCopy
  const action = role === 'company' ? step.companyAction : step.athleteAction

  const statusItems = useMemo(() => [
    'Profil repéré',
    'Intérêt envoyé',
    'Échange commencé',
    'Proposition en cours',
    'Partenariat actif',
  ], [])

  return <section className={`journey-v4 ${pageMode ? 'journey-v4--page' : ''}`}>
    <div className="journey-v4__head">
      <div>
        <span className="eyebrow">Comment ça marche</span>
        <h2>De la découverte au partenariat</h2>
        <p>Entreprise et athlète avancent étape par étape, sans complexité inutile.</p>
      </div>
      <div className="journey-role-switch" role="tablist" aria-label="Choisir un point de vue">
        <button className={role === 'company' ? 'is-active' : ''} onClick={() => { setRole('company'); setActiveIndex(0) }} role="tab" aria-selected={role === 'company'}>Côté entreprise</button>
        <button className={role === 'athlete' ? 'is-active' : ''} onClick={() => { setRole('athlete'); setActiveIndex(0) }} role="tab" aria-selected={role === 'athlete'}>Côté athlète</button>
      </div>
    </div>

    <div className="journey-stepper" role="tablist" aria-label="Étapes du parcours">
      <div className="journey-stepper__line"><span style={{ width: `${(activeIndex / (steps.length - 1)) * 100}%` }} /></div>
      {steps.map((item, index) => <button
        key={item.id}
        className={`${index === activeIndex ? 'is-active' : ''} ${index < activeIndex ? 'is-complete' : ''}`}
        onClick={() => setActiveIndex(index)}
        role="tab"
        aria-selected={index === activeIndex}
      >
        <span>{index < activeIndex ? <Check size={17} /> : index + 1}</span>
        <strong>{item.label}</strong>
      </button>)}
    </div>

    <div className="journey-stage" key={`${role}-${step.id}`}>
      <aside className="journey-stage__copy">
        <span className="journey-stage__count">Étape {activeIndex + 1}</span>
        <h3>{title}</h3>
        <p>{copy}</p>
        <ul>
          {getActions(role, activeIndex).map((item) => <li key={item}><Check size={16} />{item}</li>)}
        </ul>
        <Link className="btn btn--primary btn--md" to={getLink(role, activeIndex)}>{action}<ArrowRight size={17} /></Link>
      </aside>

      <div className="journey-stage__mockup" aria-live="polite">
        <div className="journey-window">
          <div className="journey-window__bar"><span /><span /><span /><small>{role === 'company' ? 'Espace entreprise' : 'Espace athlète'}</small></div>
          <JourneyPreview role={role} index={activeIndex} />
        </div>
      </div>

      <aside className="journey-stage__result">
        <div className="journey-result-card"><span><BadgeCheck size={18} />Résultat concret</span><p>{step.result}</p></div>
        <div className="journey-status-card">
          <strong>Statut de la relation</strong>
          {statusItems.map((item, index) => <div className={`${index === activeIndex ? 'is-active' : ''} ${index < activeIndex ? 'is-complete' : ''}`} key={item}><i>{index < activeIndex ? <Check size={12} /> : null}</i><span>{item}</span></div>)}
        </div>
      </aside>
    </div>

    <div className="journey-v4__summary">
      <div><Search /><span><strong>Découverte guidée</strong><small>Sports, villes, budgets et disponibilités.</small></span></div>
      <div><MessageCircle /><span><strong>Conversation contextualisée</strong><small>Le profil ou le brief reste visible.</small></span></div>
      <div><Handshake /><span><strong>Accord progressif</strong><small>Le contrat arrive seulement après validation.</small></span></div>
    </div>
  </section>
}

function getActions(role: JourneyRole, index: number) {
  const company = [
    ['Rechercher un profil', 'Consulter les opportunités', 'Ajouter à une sélection'],
    ['Enregistrer un profil', 'Inviter à échanger', 'Recevoir une candidature'],
    ['Poser une question', 'Partager un calendrier', 'Garder le contexte'],
    ['Fixer le budget', 'Choisir les livrables', 'Valider les échéances'],
    ['Signer le contrat', 'Effectuer le paiement', 'Suivre les actions'],
  ]
  const athlete = [
    ['Compléter son profil', 'Consulter les briefs', 'Voir les budgets'],
    ['Envoyer son intérêt', 'Poser une question', 'Recevoir une invitation'],
    ['Expliquer son projet', 'Partager ses disponibilités', 'Répondre simplement'],
    ['Ajuster les contreparties', 'Négocier les dates', 'Accepter la proposition'],
    ['Signer le contrat', 'Déposer les preuves', 'Suivre les paiements'],
  ]
  return role === 'company' ? company[index] : athlete[index]
}

function getLink(role: JourneyRole, index: number) {
  if (index === 0) return role === 'company' ? '/athletes' : '/opportunites'
  if (index === 1) return role === 'company' ? '/athletes/camille-bernard-triathlon' : '/opportunites'
  if (index === 2) return '/connexion'
  if (index === 3) return '/connexion'
  return '/inscription'
}

function JourneyPreview({ role, index }: { role: JourneyRole; index: number }) {
  const camille = athletes[0]
  const lucas = athletes[1]
  const clara = athletes[7]
  const brief = briefs.find((item) => item.status === 'active') ?? briefs[0]
  const company = companies.find((item) => item.id === brief.companyId) ?? companies[0]

  if (index === 0) return <div className="journey-preview journey-preview--discover">
    <div className="mini-search"><Search size={16} /><span>{role === 'company' ? 'Triathlon · Annecy · 5 000 €' : 'Outdoor · Rhône-Alpes'}</span><button>Rechercher</button></div>
    <div className="journey-preview__columns">
      <div><div className="mini-heading"><strong>{role === 'company' ? 'Profils disponibles' : 'Profils à compléter'}</strong><span>Voir tout</span></div><div className="mini-athletes">{[camille, lucas, clara].map((athlete) => <article key={athlete.id}><img src={athlete.cover} alt="" /><span>{athlete.firstName} {athlete.lastName}</span><small>{athlete.discipline} · {athlete.city}</small></article>)}</div></div>
      <div><div className="mini-heading"><strong>Opportunités actives</strong><span>Voir tout</span></div><article className="mini-brief"><img src={company.logoImage} alt="" /><div><strong>{brief.title}</strong><span>{company.name}</span><small>{currency(brief.budget)} · {brief.durationMonths} mois</small></div></article><article className="mini-brief"><img src={companies[6].logoImage} alt="" /><div><strong>Test terrain hiver</strong><span>{companies[6].name}</span><small>4 600 € · 6 mois</small></div></article></div>
    </div>
  </div>

  if (index === 1) return <div className="journey-preview journey-preview--interest">
    <article className="interest-profile"><img src={role === 'company' ? camille.cover : company.coverImage} alt="" /><div><span className="availability-pill"><i />Disponible</span><h4>{role === 'company' ? `${camille.firstName} ${camille.lastName}` : company.name}</h4><p>{role === 'company' ? `${camille.discipline} · ${camille.city}` : `${company.sector} · ${company.city}`}</p></div></article>
    <div className="interest-actions"><button><HeartHandshake size={17} />{role === 'company' ? 'Ajouter à ma sélection' : 'Je suis intéressé'}</button><button className="is-primary"><Send size={17} />{role === 'company' ? 'Inviter à échanger' : 'Envoyer ma candidature'}</button></div>
    <div className="interest-toast"><Check size={16} /><span>{role === 'company' ? 'Invitation envoyée à Camille' : `Candidature envoyée à ${company.name}`}</span></div>
  </div>

  if (index === 2) return <div className="journey-preview journey-preview--chat">
    <div className="chat-context"><img src={role === 'company' ? camille.photo : company.logoImage} alt="" /><div><strong>{role === 'company' ? 'Camille Bernard' : company.name}</strong><span>{role === 'company' ? 'Profil triathlon · Annecy' : brief.title}</span></div><small>Échange commencé</small></div>
    <div className="chat-thread"><div className="chat-bubble chat-bubble--other">Bonjour, votre projet nous intéresse. Êtes-vous disponible jeudi matin ?</div><div className="chat-bubble chat-bubble--self">Oui, je peux aussi partager mon calendrier et mes packs.</div><div className="chat-bubble chat-bubble--other">Parfait. Parlons du format contenu et de l’événement équipe.</div></div>
    <div className="chat-composer"><span>Écrire un message…</span><button><Send size={16} /></button></div>
  </div>

  if (index === 3) return <div className="journey-preview journey-preview--agreement">
    <div className="proposal-head"><div><span>Proposition en cours</span><h4>Partenariat saison 2026</h4></div><FileSignature /></div>
    <div className="proposal-grid"><div><small>Budget</small><strong>3 200 €</strong></div><div><small>Durée</small><strong>6 mois</strong></div><div><small>Début</small><strong>Sept. 2026</strong></div></div>
    <div className="proposal-list"><span><Check />3 publications</span><span><Check />6 stories</span><span><Check />1 événement équipe</span></div>
    <div className="proposal-footer"><span>Version 2 · modifiée aujourd’hui</span><button>Valider la proposition</button></div>
  </div>

  return <div className="journey-preview journey-preview--partnership">
    <div className="partnership-top"><div><span className="partnership-status"><i />Partenariat actif</span><h4>Alpine Vision × Camille Bernard</h4></div><Handshake /></div>
    <div className="partnership-progress"><div><span>Avancement</span><strong>64 %</strong></div><i><b style={{ width: '64%' }} /></i></div>
    <div className="partnership-cards"><article><FileSignature /><span>Contrat<strong>Signé</strong></span></article><article><CircleDollarSign /><span>Paiement<strong>Versé</strong></span></article><article><UserRoundCheck /><span>Livrables<strong>3 à venir</strong></span></article></div>
    <div className="partnership-next"><Sparkles /><div><strong>Prochaine action</strong><span>Valider la vidéo produit avant le 18 septembre.</span></div><button>Ouvrir</button></div>
  </div>
}
